import React, { useEffect, useMemo, useState } from 'react';
import api from '../../services/api';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import ContentRichTextEditor from '../../components/admin/ContentRichTextEditor';
import {
  NEWS_CATEGORIES,
  NewsCategory,
  normalizeNewsCategory,
} from '../../constants/newsCategories';
import {
  formatChinaDate,
  formatChinaDateInput,
  parseChinaDateInput,
  getChinaTodayDateInput,
} from '../../utils/date';

// React 19 compatibility: react-quill previously relied on removed ReactDOM.findDOMNode
// Override getEditingArea to use refs directly instead of findDOMNode
const AnyReactQuill: any = ReactQuill;
if (
  AnyReactQuill &&
  AnyReactQuill.prototype &&
  !AnyReactQuill.prototype.__patchedNoFindDomNode
) {
  AnyReactQuill.prototype.getEditingArea = function getEditingAreaPatched() {
    const editor = this.editingArea;
    if (!editor) return null;
    // Direct DOM element
    if (editor instanceof Element) return editor;
    // React ref object
    if (editor && 'current' in editor) {
      return editor.current;
    }
    return null;
  };
  AnyReactQuill.prototype.__patchedNoFindDomNode = true;
}

type NewsItem = {
  _id?: string;
  title: string;
  category: NewsCategory;
  coverUrl?: string;
  publishedAt?: string;
  isFeatured?: boolean;
  isVisible?: boolean;
  content?: string; // HTML
};

const emptyNews: NewsItem = {
  title: '',
  category: 'activity',
  coverUrl: '',
  publishedAt: '',
  isFeatured: false,
  isVisible: true,
  content: '',
};

type InlineRichTextEditorProps = {
  label: string;
  value: string;
  onChange: (html: string) => void;
};

const InlineRichTextEditor: React.FC<InlineRichTextEditorProps> = ({
  label,
  value,
  onChange,
}) => {
  const modules = useMemo(
    () => ({
      toolbar: [
        ['bold', 'italic', 'underline'],
        [{ script: 'sub' }, { script: 'super' }],
        ['clean'],
      ],
    }),
    []
  );

  const formats = ['bold', 'italic', 'underline', 'script'];

  return (
    <div>
      <label className="mb-1 block font-medium text-neutral-700">{label}</label>
      <div className="rounded-md border border-neutral-300 bg-white">
        <ReactQuill
          className="inline-richtext-editor"
          theme="snow"
          value={value}
          onChange={onChange}
          modules={modules}
          formats={formats}
        />
      </div>
    </div>
  );
};

const stripHtml = (value: string = '') =>
  value.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();

const AdminNews: React.FC = () => {
  const [items, setItems] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<NewsItem | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploadingCover, setUploadingCover] = useState(false);

  const loadNews = async () => {
    try {
      setLoading(true);
      const res = await api.get<NewsItem[]>('/news');
      setItems(
        (res.data || []).map((item) => ({
          ...item,
          category: normalizeNewsCategory(item.category),
        }))
      );
    } catch (err) {
      console.error(err);
      setError('Failed to load news list.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadNews();
  }, []);

  const openCreate = () => {
    setEditing({ ...emptyNews, publishedAt: getChinaTodayDateInput() });
    setError(null);
  };

  const openEdit = (item: NewsItem & { summary?: string }) => {
    const { summary: _removed, ...rest } = item;
    setEditing({
      ...rest,
      category: normalizeNewsCategory(rest.category),
      publishedAt: rest.publishedAt ? formatChinaDateInput(rest.publishedAt) : '',
    });
    setError(null);
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    if (!editing) return;
    const target = e.target;
    const { name, value } = target;

    if (target instanceof HTMLInputElement && target.type === 'checkbox') {
      setEditing({ ...editing, [name]: target.checked });
    } else {
      setEditing({ ...editing, [name]: value });
    }
  };

  const handleSave = async () => {
    if (!editing) return;
    if (!editing.title) {
      setError('Please enter a title.');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const payload = {
        title: editing.title,
        category: normalizeNewsCategory(editing.category),
        coverUrl: editing.coverUrl || undefined,
        publishedAt: parseChinaDateInput(editing.publishedAt),
        isFeatured: !!editing.isFeatured,
        isVisible: editing.isVisible ?? true,
        content: editing.content || '',
      };

      if (editing._id) {
        await api.put(`/news/${editing._id}`, payload);
      } else {
        await api.post('/news', payload);
      }

      setEditing(null);
      await loadNews();
    } catch (err) {
      console.error(err);
      setError('Save failed. Please try again later.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item: NewsItem) => {
    if (!item._id) return;
    const ok = window.confirm(`Are you sure you want to delete "${item.title}"?`);
    if (!ok) return;
    try {
      await api.delete(`/news/${item._id}`);
      await loadNews();
    } catch (err) {
      console.error(err);
      alert('Delete failed. Please try again later.');
    }
  };

  const handleCoverFileChange = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (!editing) return;
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      setUploadingCover(true);
      const res = await api.post<{ url?: string; path?: string }>(
        '/uploads/news-cover',
        formData,
        { timeout: 60000 }
      );
      const rawPath = res.data.path || '';
      let url = res.data.url || rawPath || '';
      // Keep same behavior as editor uploads by resolving relative paths via API origin
      if (rawPath) {
        const base = (api.defaults.baseURL as string | undefined) || '';
        const origin = base.startsWith('http')
          ? new URL(base).origin
          : window.location.origin;
        url = new URL(rawPath, origin).toString();
      }
      setEditing((prev) => (prev ? { ...prev, coverUrl: url } : prev));
    } catch (err) {
      console.error(err);
      // eslint-disable-next-line no-alert
      alert('Cover upload failed. Please try again later.');
    } finally {
      setUploadingCover(false);
      e.target.value = '';
    }
  };

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h1 className="font-serif text-2xl font-semibold text-neutral-900">
          News Management
        </h1>
        <button
          onClick={openCreate}
          className="rounded-full bg-brand-red px-4 py-1.5 text-xs font-semibold tracking-[0.18em] text-white hover:bg-red-700"
        >
          Add News
        </button>
      </div>

      {loading ? (
        <p className="text-sm text-neutral-600">Loading...</p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-neutral-200 bg-white shadow-subtle">
          <table className="min-w-full text-left text-xs">
            <thead className="border-b border-neutral-200 bg-neutral-50">
              <tr>
                <th className="px-3 py-2 font-semibold text-neutral-700">
                  Title
                </th>
                <th className="px-3 py-2 font-semibold text-neutral-700">
                  Category
                </th>
                <th className="px-3 py-2 font-semibold text-neutral-700">
                  Published At
                </th>
                <th className="px-3 py-2 font-semibold text-neutral-700">
                  Featured
                </th>
                <th className="px-3 py-2 font-semibold text-neutral-700">
                  Visible
                </th>
                <th className="px-3 py-2 font-semibold text-neutral-700">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {items.map((n) => (
                <tr key={n._id} className="border-b border-neutral-100">
                  <td className="max-w-xs px-3 py-2 text-neutral-900">
                    <span className="line-clamp-2">{stripHtml(n.title)}</span>
                  </td>
                  <td className="px-3 py-2 text-neutral-700">{n.category}</td>
                  <td className="px-3 py-2 text-neutral-700">
                    {n.publishedAt ? formatChinaDate(n.publishedAt) : '-'}
                  </td>
                  <td className="px-3 py-2 text-neutral-700">
                    {n.isFeatured ? 'Yes' : 'No'}
                  </td>
                  <td className="px-3 py-2 text-neutral-700">
                    {n.isVisible ?? true ? 'Yes' : 'No'}
                  </td>
                  <td className="px-3 py-2">
                    <button
                      onClick={() => openEdit(n)}
                      className="mr-2 text-xs text-brand-red hover:underline"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(n)}
                      className="text-xs text-neutral-500 hover:text-red-600 hover:underline"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr>
                  <td
                    colSpan={6}
                    className="px-3 py-4 text-center text-xs text-neutral-500"
                  >
                    No news yet. Click Add News in the top-right corner.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Edit / Create dialog */}
      {editing && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/30">
          <div className="max-h-[94vh] w-full max-w-4xl overflow-y-auto rounded-lg bg-white p-6 shadow-subtle">
            <h2 className="mb-4 text-sm font-semibold text-neutral-900">
              {editing._id ? 'Edit News' : 'Add News'}
            </h2>
            <div className="space-y-3 text-xs">
              <InlineRichTextEditor
                label="Title"
                value={editing.title}
                onChange={(html) =>
                  setEditing((prev) => (prev ? { ...prev, title: html } : prev))
                }
              />
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mb-1 block font-medium text-neutral-700">
                    Category
                  </label>
                  <select
                    name="category"
                    value={editing.category}
                    onChange={handleChange}
                    className="um-input w-full bg-white px-2 py-1.5"
                  >
                    {NEWS_CATEGORIES.map((c) => (
                      <option key={c.value} value={c.value}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="mb-1 block font-medium text-neutral-700">
                    Published At
                  </label>
                  <input
                    type="date"
                    name="publishedAt"
                    value={editing.publishedAt || ''}
                    onChange={handleChange}
                    className="w-full rounded-md border border-neutral-300 px-2 py-1.5 text-xs outline-none focus:border-brand-red focus:ring-1 focus:ring-brand-red"
                  />
                </div>
              </div>
              <div>
                <label className="mb-1 block font-medium text-neutral-700">
                  Cover Image
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleCoverFileChange}
                    className="text-xs"
                  />
                  {uploadingCover && (
                    <span className="text-[11px] text-neutral-500">
                      Uploading...
                    </span>
                  )}
                </div>
                {editing.coverUrl && (
                  <div className="mt-2 h-24 w-40 overflow-hidden rounded-md border border-neutral-200 bg-neutral-100">
                    <img
                      src={editing.coverUrl}
                      alt="Cover preview"
                      className="h-full w-full object-cover"
                    />
                  </div>
                )}
                <p className="mt-1 text-[10px] text-neutral-500">
                  Upload image files to generate and save accessible URLs for this news item.
                </p>
              </div>
              <div className="flex gap-4">
                <label className="flex items-center gap-1 text-xs text-neutral-800">
                  <input
                    type="checkbox"
                    name="isFeatured"
                    checked={!!editing.isFeatured}
                    onChange={handleChange}
                    className="um-check h-3 w-3"
                  />
                  Set as featured news
                </label>
                <label className="flex items-center gap-1 text-xs text-neutral-800">
                  <input
                    type="checkbox"
                    name="isVisible"
                    checked={editing.isVisible ?? true}
                    onChange={handleChange}
                    className="um-check h-3 w-3"
                  />
                  Visible on frontend
                </label>
              </div>
              <ContentRichTextEditor
                label="Content"
                value={editing.content || ''}
                onChange={(html: string) =>
                  setEditing((prev) => (prev ? { ...prev, content: html } : prev))
                }
              />
            </div>
            {error && (
              <p className="mt-2 text-[11px] text-red-600">{error}</p>
            )}
            <div className="mt-4 flex justify-end gap-2">
              <button
                onClick={() => setEditing(null)}
                className="rounded-full border border-neutral-300 px-4 py-1 text-xs text-neutral-700 hover:bg-neutral-100"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="rounded-full bg-brand-red px-4 py-1 text-xs font-semibold text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-70"
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminNews;

